import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { SearchInput } from 'ui/standard/input/search_input/search_input';
import { GlobalLocaleStore, LOCALE_LIST, LOCALE_MAP, LocaleData } from 'services/base/i18n';
import baseStyles from './locale_selector.module.css';
import { LocaleSelectorStore } from './locale_selector_store';

type LocaleSelectorStyles = {
  inputButton: string;
  itemRow: string;
  itemContainer: string;
  itemLabel: string;
};

const themedStyles: ThemedStyles<LocaleSelectorStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.inputButton,
    itemRow: baseStyles.itemRow,
    itemContainer: baseStyles.itemContainer,
    itemLabel: baseStyles.itemLabel,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.inputButton,
    itemRow: baseStyles.itemRow,
    itemContainer: baseStyles.itemContainer,
    itemLabel: baseStyles.itemLabel,
  },
};

const LocaleDisplay = observer(({ store }: { store: LocaleSelectorStore}) => {
  const styles = useStyles(themedStyles);
  const locale = GlobalLocaleStore.get();
  const data = LOCALE_MAP[locale];
  return (
    <div className={styles.inputButton} onClick={store.search.modal.showModal}>
      {data.label}
    </div>
  );
});

type LocaleItemProps = {
  store: LocaleSelectorStore;
  data: LocaleData;
  key: string;
};

const LocaleItem = ({ store, data }: LocaleItemProps) => {
  const styles = useStyles(themedStyles);
  const onClick = React.useCallback(action(() => {
    store.search.modal.hideModal();
    GlobalLocaleStore.set(data.code);
  }), [data]);

  return (
    <div className={styles.itemRow}>
      <div className={styles.itemContainer} onClick={onClick}>
        <div className={styles.itemLabel}>
          {data.label}
        </div>
      </div>
    </div>
  );
};

const ALWAYS_TRUE = () => true;
const LOCALE_CODE = (item: LocaleData) => item.code;

type LocaleSelectorProps = {
  store: LocaleSelectorStore;
};

export const BaseLocaleSelector = observer(({ store }: LocaleSelectorProps) => {
  const BoundLocaleDisplay = React.useCallback(() => (
    <LocaleDisplay store={store}/>
  ), [store]);

  const BoundLocaleItem = React.useCallback((props: Omit<LocaleItemProps, 'store'>) => (
    <LocaleItem {...props} store={store}/>
  ), [store]);

  return (
    <SearchInput
      placeholder=''
      data={LOCALE_LIST}
      store={store.search}
      keyFunc={LOCALE_CODE}
      filterFunc={ALWAYS_TRUE}
      ItemComponent={BoundLocaleItem}
      DisplayComponent={BoundLocaleDisplay}
    />
  );
});

@observer
export class LocaleSelector extends React.Component {
  store: LocaleSelectorStore;
  cleanup?(): void;

  constructor(props: object) {
    super(props);
    this.store = new LocaleSelectorStore();
  }

  render() {
    return <BaseLocaleSelector store={this.store}/>;
  }
}
